import isNull from "lodash/isNull";
import { useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { shallow } from "zustand/shallow";

import { bugNotif } from "../../api/bugsnag";
import { getErrorMsg } from "../../../Common/utils/common";
import { getSurveyQuestionnaireByChallengeKey } from "../../services/surveyQuestionnaire";
import { SurveyChallengeKeys } from "../../../Common/models/challengesAndRewards";
import { useWebUserStore } from "../../CommonStores/user/users";
import { SurveyQuestionnaire } from "../../../Common/domain/entities/features/surveyQuestionnaire/entity";
import {
  MultipleChoiceQuestion,
  EssayQuestion,
} from "../../../Common/domain/entities/features/surveyQuestionnaire/dto";
import { useSurveyQuestionnaireStore } from "./store";
import { BaseSurveyResult } from "../../../Common/domain/entities/features/surveyResults/dto";
import { useWLLoaderStore } from "../../../Common/components/WLLoader/store";
import { createSurveyResults } from "../../api/survey";

export const FETCH_SURVEY_QUESTIONNAIRE_QUERY_KEY =
  "FETCH_SURVEY_QUESTIONNAIRE";

export const useSurveyQuestionnaireProvider = (
  challengeKey: SurveyChallengeKeys = "memberSurvey"
) => {
  const user = useWebUserStore((store) => store.user, shallow);

  const {
    data: surveyQuestionnaire,
    error: surveyQuestionnaireError,
    isLoading: isLoadingSurveyQuestionnaire,
  } = useQuery<SurveyQuestionnaire | null>({
    queryKey: [FETCH_SURVEY_QUESTIONNAIRE_QUERY_KEY, challengeKey, user?.docId],
    queryFn: async () => {
      const resp = await getSurveyQuestionnaireByChallengeKey(challengeKey);
      return resp;
    },
    enabled: !!user,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isNull(surveyQuestionnaireError)) {
      bugNotif(
        "useSurveyQuestionnaireProvider - getSurveyQuestionnaireByChallengeKey",
        getErrorMsg(surveyQuestionnaireError).rawErrorMsg
      );
    }
  }, [surveyQuestionnaireError]);

  return {
    surveyQuestionnaire,
    isLoadingSurveyQuestionnaire,
    surveyQuestionnaireError,
  };
};

export const useSurveyQuestionnaireActionsProvider = (
  surveyQuestionnaire: SurveyQuestionnaire | null | undefined
) => {
  const { user } = useWebUserStore();
  const { setLoading } = useWLLoaderStore();
  const {
    currentSectionIndex,
    setCurrentSectionIndex,
    answers,
    updateAnswer,
    setToastError,
    setIsConfirmationModalOpen,
    setIsSubmissionSuccess,
  } = useSurveyQuestionnaireStore();

  // Get current section
  const currentSection = useMemo(() => {
    if (
      !surveyQuestionnaire?.sections ||
      surveyQuestionnaire.sections.length === 0
    ) {
      return null;
    }
    return surveyQuestionnaire.sections[currentSectionIndex] || null;
  }, [surveyQuestionnaire, currentSectionIndex]);

  // Check if current section is the last one
  const isLastSection = useMemo(() => {
    if (!surveyQuestionnaire?.sections) return false;
    return currentSectionIndex === surveyQuestionnaire.sections.length - 1;
  }, [surveyQuestionnaire, currentSectionIndex]);

  // Get answer value for a question
  const getAnswerValue = (
    sectionIndex: number,
    questionIndex: number
  ): string | number => {
    const answerKey = `${sectionIndex}-${questionIndex}`;
    const value = answers[answerKey];
    // Handle the case where value is 0 (first option) - 0 is falsy but valid
    return value !== undefined && value !== null ? value : "";
  };

  // Helper function to validate a single question
  const validateQuestion = (
    question: MultipleChoiceQuestion | EssayQuestion,
    answer: string | number | undefined
  ): boolean => {
    // For multiple choice, check if answer exists (including 0)
    if (question.type === "multipleChoice") {
      return answer !== undefined && answer !== null;
    }

    // For essay, check if answer is not empty string
    if (question.type === "essay") {
      return (
        answer !== undefined &&
        answer !== null &&
        typeof answer === "string" &&
        answer.trim().length > 0
      );
    }

    return false;
  };

  // Check if all questions in current section are answered
  const validateCurrentSection = (): boolean => {
    if (!currentSection) return false;

    return currentSection.questions.every((question) => {
      const answerKey = `${currentSectionIndex}-${question.questionIndex}`;
      const answer = answers[answerKey];
      return validateQuestion(question, answer);
    });
  };

  // Check if all questions in all sections are answered
  const validateAllSections = (): boolean => {
    if (!surveyQuestionnaire?.sections) return false;

    return surveyQuestionnaire.sections.every((section, sectionIndex) => {
      return section.questions.every((question) => {
        const answerKey = `${sectionIndex}-${question.questionIndex}`;
        const answer = answers[answerKey];
        return validateQuestion(question, answer);
      });
    });
  };

  // Transform answers to BaseSurveyResult format
  const transformAnswersToResults = (): BaseSurveyResult[] => {
    if (!surveyQuestionnaire?.sections) return [];

    const results: BaseSurveyResult[] = [];

    surveyQuestionnaire.sections.forEach((section, sectionIndex) => {
      section.questions.forEach((question) => {
        const answerKey = `${sectionIndex}-${question.questionIndex}`;
        const answer = answers[answerKey];

        if (answer !== undefined && answer !== null) {
          let answerString: string;

          // For multiple choice, map answer index to option text
          if (question.type === "multipleChoice") {
            const multipleChoiceQuestion = question as MultipleChoiceQuestion;
            // Answer is the optionIndex (number)
            if (typeof answer === "number" && multipleChoiceQuestion.options) {
              const optionIndex = answer;
              // If it's a valid index, use the option text
              if (
                optionIndex >= 0 &&
                optionIndex < multipleChoiceQuestion.options.length
              ) {
                answerString =
                  multipleChoiceQuestion.options[optionIndex].choiceText;
              } else {
                // Fallback to string if index is invalid
                answerString = String(answer);
              }
            } else {
              // Fallback if answer is not a number
              answerString = String(answer);
            }
          } else {
            // For essay, answer is already a string
            answerString = typeof answer === "string" ? answer : String(answer);
          }

          results.push({
            questionType: question.type,
            sectionIndex,
            questionIndex: question.questionIndex,
            answer: answerString,
          });
        }
      });
    });

    return results;
  };

  // Handle answer change for multiple choice
  const handleMultipleChoiceChange = (
    sectionIndex: number,
    questionIndex: number,
    value: string | number
  ) => {
    // Normalize to number since optionIndex is always a number
    const normalizedValue = typeof value === "string" ? Number(value) : value;
    updateAnswer(sectionIndex, questionIndex, normalizedValue);
  };

  // Handle answer change for essay
  const handleEssayChange = (
    sectionIndex: number,
    questionIndex: number,
    value: string
  ) => {
    updateAnswer(sectionIndex, questionIndex, value);
  };

  // Handle continue to next section
  const handleContinue = () => {
    if (!surveyQuestionnaire?.sections) return;

    // Validate current section before continuing
    if (!validateCurrentSection()) {
      setToastError("Please answer all questions before continuing.");
      return;
    }

    if (currentSectionIndex < surveyQuestionnaire.sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  // Handle submit survey - shows confirmation modal
  const handleSubmit = () => {
    // Validate all sections before showing confirmation modal
    if (!validateAllSections()) {
      setToastError("Please answer all questions before submitting.");
      return;
    }

    // Check if we have required data
    if (!surveyQuestionnaire?.docId || !user?.docId) {
      setToastError(
        "Missing required information. Please refresh and try again."
      );
      return;
    }

    // Show confirmation modal
    setIsConfirmationModalOpen(true);
  };

  // Actually submit the survey (called from confirmation modal)
  const handleConfirmSubmit = () => {
    if (!surveyQuestionnaire?.docId || !user?.docId) {
      setToastError(
        "Missing required information. Please refresh and try again."
      );
      return;
    }

    // Transform answers to results format
    const results = transformAnswersToResults();

    // Submit survey
    onSubmitSurvey({
      surveyId: surveyQuestionnaire.docId,
      userId: user.docId,
      results,
    });
  };

  // Mutation for submitting survey
  const { mutate: onSubmitSurvey } = useMutation({
    mutationFn: createSurveyResults,
    onMutate: () => {
      setLoading(true, "Submitting survey...");
    },
    onSuccess: () => {
      setLoading(false);
      setToastError("");
      setIsSubmissionSuccess(true);
    },
    onError: (err: unknown) => {
      setLoading(false);
      const { errorMsg, rawErrorMsg } = getErrorMsg(err);
      setToastError(errorMsg || "Failed to submit survey. Please try again.");
      bugNotif("handleSubmit - createSurveyResult", rawErrorMsg);
    },
  });

  return {
    currentSection,
    isLastSection,
    getAnswerValue,
    handleMultipleChoiceChange,
    handleEssayChange,
    handleContinue,
    handleSubmit,
    handleConfirmSubmit,
  };
};
